import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";

function CardMyPost({ id, title, type = "post", onClick, onDelete, onDeleteSuccess, onRequestDelete, active = false }) {
    const navigate = useNavigate();
    const [showMenu, setShowMenu] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const menuRef = useRef(null);

    // 메뉴 외부 클릭 감지
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (menuRef.current && !menuRef.current.contains(event.target)) {
                setShowMenu(false);
            }
        };

        if (showMenu) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showMenu]);

    const handleClick = (e) => {
        // 메뉴 버튼 클릭은 무시
        if (e.target.closest('.menu-button') || e.target.closest('.menu-dropdown')) {
            return;
        }

        if (onClick) {
            onClick();
        } else if (type === "post" && id) {
            navigate(`/mypost-post?id=${id}`);
        }
    };

    const handleMenuClick = (e) => {
        e.stopPropagation();
        setShowMenu(!showMenu);
    };

    const handleExport = async (e) => {
        e.stopPropagation();
        setShowMenu(false);
        
        try {
            const { exportPostAsMarkdown } = await import('../../utils/exportMarkdown');
            await exportPostAsMarkdown(id);
        } catch (err) {
            alert(`게시글을 내보내는 중 오류가 발생했습니다: ${err.message}`);
            console.error("마크다운 내보내기 실패:", err);
        }
    };

    const handleDelete = async (e) => {
        e.stopPropagation();
        
        if (!id) return;

        // 부모 컴포넌트에서 삭제 요청을 처리하도록 위임 (커스텀 팝업 등 사용 시)
        if (onRequestDelete) {
            setShowMenu(false);
            onRequestDelete(id, title);
            return;
        }

        console.log('[CardMyPost] 삭제 시작 - ID:', id, '타입:', typeof id);

        const confirmDelete = window.confirm(`"${title || '제목 없음'}" 페이지를 삭제하시겠습니까?\n\n이 작업은 되돌릴 수 없습니다.`);
        if (!confirmDelete) {
            setShowMenu(false);
            return;
        }

        try {
            setDeleting(true);
            
            // 커스텀 onDelete가 있으면 사용
            if (onDelete) {
                await onDelete(id);
            } else {
                // 기본 삭제 API 호출
                const { deleteJson } = await import('../../services/apiClient');
                const result = await deleteJson(`/api/post?id=${id}`);

                if (!result.success) {
                    throw new Error(result.error || result.message || "삭제 실패");
                }
                
                console.log('[CardMyPost] 삭제 성공 - ID:', id);
            }

            // 삭제 성공 콜백
            if (onDeleteSuccess) {
                onDeleteSuccess(id);
            }

            setShowMenu(false);
        } catch (err) {
            console.error("페이지 삭제 실패:", err);
            alert("페이지를 삭제하는 중 오류가 발생했습니다: " + err.message);
        } finally {
            setDeleting(false);
        }
    };

    return (
        <div
            className="card-mypost"
            onClick={handleClick}
            style={{
                backgroundColor: active ? "var(--color-primary)" : "var(--color-bg-card)",
                color: active ? "var(--color-bg" : "var(--color-text, #000000)",
                transition: "background-color 0.2s ease, color 0.2s ease"
            }}
            onMouseEnter={(e) => {
                if (!deleting && !active) {
                    e.currentTarget.style.backgroundColor = "var(--color-bg-hover)";
                }
            }}
            onMouseLeave={(e) => {
                if (!deleting && !active) {
                    e.currentTarget.style.backgroundColor = "var(--color-bg-card)";
                } else if (active) {
                    e.currentTarget.style.backgroundColor = "var(--color-primary)";
                }
            }}
        >
            <div style={{ display: "flex", alignItems: "center", flex: 1, overflow: "hidden" }}>
                {type === "add" ? (
                    <>
                        <span
                            className="material-symbols-rounded"
                            style={{
                                fontSize: "20px",
                                marginRight: "8px",
                                color: "var(--color-text-secondary, #666)",
                            }}
                        >
                            add
                        </span>
                        <span
                            style={{
                                color: "var(--color-text-secondary, #666)",
                                fontSize: "14px",
                            }}
                        >
                            새 페이지 추가
                        </span>
                    </>
                ) : (
                    <>
                        <span
                            className="material-symbols-rounded"
                            style={{
                                fontSize: "20px",
                                marginRight: "8px",
                                color: active ? "var(--color-bg)" : "var(--color-text-secondary)",
                            }}
                        >
                            article
                        </span>
                        <span
                            style={{
                                fontSize: "14px",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                color: active ? "var(--color-bg)" : "inherit",
                            }}
                        >
                            {deleting ? "삭제 중..." : (title || "제목 없음")}
                        </span>
                    </>
                )}
            </div>

            {/* 메뉴 버튼 (add 타입이 아닐 때만 표시) */}
            {type !== "add" && type !== "folder" && (
                <div ref={menuRef} style={{ position: "relative" }}>
                    <button
                        className="menu-button"
                        onClick={handleMenuClick}
                        disabled={deleting}
                        style={{
                            width: "24px",
                            height: "24px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            border: "none",
                            background: "transparent",
                            cursor: deleting ? "not-allowed" : "pointer",
                            borderRadius: "4px",
                            transition: "background-color 0.2s ease",
                            padding: 0,
                        }}
                        onMouseEnter={(e) => {
                            if (!deleting) {
                                e.currentTarget.style.backgroundColor = "var(--color-monotone-4)";
                            }
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = "transparent";
                        }}
                    >
                        <span
                            className="material-symbols-rounded"
                            style={{
                                fontSize: "18px",
                                color: active ? "var(--color-bg)" : "var(--color-text-secondary)",
                            }}
                        >
                            more_vert
                        </span>
                    </button>

                    {/* 드롭다운 메뉴 */}
                    {showMenu && (
                        <div
                            className="menu-dropdown"
                            style={{
                                position: "absolute",
                                top: "calc(100% + 4px)",
                                right: 0,
                                minWidth: "120px",
                                backgroundColor: "var(--color-bg)",
                                border: "1px solid var(--color-border)",
                                borderRadius: "4px",
                                boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
                                zIndex: 1000,
                                overflow: "hidden",
                            }}
                        >
                            <button
                                onClick={handleExport}
                                style={{
                                    width: "100%",
                                    padding: "8px 12px",
                                    border: "none",
                                    background: "transparent",
                                    textAlign: "left",
                                    cursor: "pointer",
                                    fontSize: "14px",
                                    color: "var(--color-text)",
                                    transition: "background-color 0.2s ease",
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.backgroundColor = "var(--color-bg-hover)";
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.backgroundColor = "transparent";
                                }}
                            >
                                <span className="material-symbols-rounded" style={{ fontSize: "16px", marginRight: "8px", verticalAlign: "middle" }}>
                                    download
                                </span>
                                내보내기
                            </button>
                            <button
                                onClick={handleDelete}
                                disabled={deleting}
                                style={{
                                    width: "100%",
                                    padding: "8px 12px",
                                    border: "none",
                                    background: "transparent",
                                    textAlign: "left",
                                    cursor: deleting ? "not-allowed" : "pointer",
                                    fontSize: "14px",
                                    color: "var(--color-failure)",
                                    transition: "background-color 0.2s ease",
                                }}
                                onMouseEnter={(e) => {
                                    if (!deleting) {
                                        e.currentTarget.style.backgroundColor = "var(--color-bg-hover)";
                                    }
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.backgroundColor = "transparent";
                                }}
                            >
                                <span className="material-symbols-rounded" style={{ fontSize: "16px", marginRight: "8px", verticalAlign: "middle" }}>
                                    delete
                                </span>
                                삭제
                            </button>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

export default CardMyPost;

